import { Link } from "react-router-dom";
import { Download, ArrowRight, Activity, Wifi, Bell, Hand, Brain, FlaskConical, Fingerprint, RefreshCw, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Section";
import { BigStat, BarChart } from "@/components/ResearchViz";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const WHITEPAPER = "/WearSpray-Research-White-Paper.pdf";

const LOOP = [
  { k: "Sense", Icon: Activity, c: "#00e5ff", d: "Understand environmental and personal context: UV, location, heat, activity, potential perspiration and time." },
  { k: "Understand", Icon: Wifi, c: "#3ad6a0", d: "The intelligence layer combines those signals with what it learns about the user: routines, activities, skin goals, preferred care and previous applications." },
  { k: "Prompt", Icon: Bell, c: "#e6c068", d: "Context, not another notification. \u201CUV exposure is high and you have been outside for 90 minutes. Consider reapplying.\u201D" },
  { k: "Act", Icon: Hand, c: "#ff2d78", d: "The product is already on the wrist. No bottle to retrieve, no bag to search. Press. Dispense. Apply. Continue." },
  { k: "Learn", Icon: Brain, c: "#b794f6", d: "Every application and check-in teaches WearSpray, so prompts get more personal and better timed over time." },
];

const DOCTRINE = [
  {
    key: "evidence",
    Icon: FlaskConical,
    c: "#00e5ff",
    title: "Evidence Over Guesswork",
    d: "Every concept traces back to real dermatology and wearable-sensor research, not a hunch about what skin needs.",
  },
  {
    key: "baseline",
    Icon: Fingerprint,
    c: "#b794f6",
    title: "Your Baseline, Not Everyone's Average",
    d: "Skin is personal. The model is built to learn your patterns and your normal, not a population-wide guess.",
  },
  {
    key: "loop",
    Icon: RefreshCw,
    c: "#3ad6a0",
    title: "Built to Close the Loop",
    d: "Insight without action is trivia. Every signal the model reads is designed to connect straight back to the wrist.",
  },
  {
    key: "privacy",
    Icon: ShieldCheck,
    c: "#e6c068",
    title: "Privacy Is Not Optional",
    d: "Skin, location and routine data stay under the user's control, always visible, always theirs to limit.",
  },
];

function DoctrineCard({ Icon, c, title, d, delay }) {
  return (
    <Reveal delay={delay}>
      <div className="h-full border border-zinc-800 bg-[#111] p-7" data-testid={`skin-intel-doctrine-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <div className="w-12 h-12 rounded-full border flex items-center justify-center" style={{ borderColor: c, color: c }}>
          <Icon size={22} strokeWidth={1.6} />
        </div>
        <h3 className="font-heading text-lg font-medium tracking-tight mt-5" style={{ color: c }}>{title}</h3>
        <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">{d}</p>
      </div>
    </Reveal>
  );
}

function SkinIntelligence() {
  return (
    <section className="relative border-b border-zinc-800 bg-black overflow-hidden" data-testid="skin-intel-section">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,229,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.5) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">
            Personal Skin Intelligence · Understand Layer
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
            Skin science,{" "}
            <span className="text-accent">taken personally.</span>
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
            Behind every prompt is a discipline: real dermatology and
            cosmetic-science research, and a model built to learn one
            person's skin, not everyone's average. Here is the doctrine
            guiding that work.
          </p>
        </Reveal>

        {/* DOCTRINE */}
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-16 mb-6">The Doctrine</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DOCTRINE.map((item, i) => (
            <DoctrineCard key={item.key} Icon={item.Icon} c={item.c} title={item.title} d={item.d} delay={i * 0.06} />
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="font-body text-zinc-400 text-base leading-relaxed mt-14 max-w-3xl">
            See the doctrine in motion, ten concept previews of the AI
            consultant's console, on the{" "}
            <Link to="/" className="text-accent hover:underline">homepage</Link>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Science() {
  return (
    <div className="bg-black text-white min-h-screen" data-testid="science-page">
      <SEO {...PAGE_SEO["/science"]} path="/science" />
      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(0,229,255,0.10), transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24 md:pt-48 md:pb-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-8">
              The Science · Behavioral Evidence
            </span>
            <h1 className="font-heading font-light tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl max-w-5xl">
              The gap is not protection.{" "}
              <span style={{ color: "#ff2d78" }}>It is action.</span>
            </h1>
            <p className="font-body text-zinc-400 text-lg md:text-xl leading-relaxed mt-10 max-w-3xl">
              Consumers understand sun protection. The evidence shows the real
              challenge is behavioral: remembering, knowing when, and having care
              available at the moment it is needed.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <a
              href={WHITEPAPER}
              download
              data-testid="science-hero-whitepaper"
              className="group inline-flex items-center gap-2.5 font-body text-sm font-medium bg-[#00e5ff] text-black px-7 py-4 mt-10 hover:bg-[#00b3cc] transition-colors"
            >
              <Download size={17} />
              Download the Research White Paper
            </a>
          </Reveal>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-zinc-800" data-testid="science-problem">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">The Problem</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
              Sunscreen works.{" "}
              <span className="text-accent">Reapplication breaks down.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
              A 2023 nationwide United States study of 2,283 regular SPF consumers
              found that 20% to 60% of regular SPF users do not reapply, depending
              on the type of SPF product. Reapplication behavior varied with
              weather, time in the sun, activity and sweating.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 mt-16 items-center">
            <Reveal>
              <BigStat value="20-60" caption="of regular SPF users do not reapply, depending on product category" color="#ff2d78" size="xl" testid="science-stat-reapply" />
            </Reveal>
            <Reveal delay={0.1}>
              <BarChart
                title="Reapplication rate by condition"
                testid="science-chart-reapply"
                bars={[
                  { label: "Sunny · SPF skincare", value: 58, color: "#00e5ff", highlight: true },
                  { label: "Sunny · SPF cosmetics", value: 37, color: "#6366f1" },
                  { label: "Partly cloudy · skincare", value: 24, color: "#00e5ff" },
                  { label: "Partly cloudy · cosmetics", value: 17, color: "#6366f1" },
                ]}
              />
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <p className="font-body text-xs text-zinc-500 leading-relaxed mt-14 max-w-3xl border-l border-zinc-700 pl-5">
              Source: Norman et al., <em>Food and Chemical Toxicology</em>, November 2023.
              DOI: 10.1016/j.fct.2023.114093. Research affiliations included{" "}
              <span className="text-zinc-300">Beiersdorf, L'Oréal USA, Procter &amp; Gamble, Revlon and the Personal Care Products Council.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* HUMAN CONSEQUENCE */}
      <section className="border-b border-zinc-800 bg-[#0a0a0a]" data-testid="science-consequence">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">The Human Consequence</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
              We know we should protect our skin. Many of us{" "}
              <span className="text-accent">regret that we did not.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
              La Roche-Posay and L'Oréal reported results from the HELIOS
              epidemiological study, an Ipsos online-panel survey of approximately
              17,000 people across 17 countries.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 mt-16">
            <Reveal>
              <BigStat value="57.6" caption="regret not protecting themselves from the sun in the past" color="#ff2d78" size="xl" testid="science-stat-regret" />
            </Reveal>
            <Reveal delay={0.1}>
              <BigStat value="12" caption="systematically or frequently use all recommended forms of sun protection, despite 88% awareness of the risks" color="#e6c068" size="xl" testid="science-stat-yearround" />
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="mt-20 border-l-2 border-accent pl-6 md:pl-8 max-w-3xl">
              <p className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white">
                Protection is understood. Action is inconsistent. Regret comes later.
              </p>
              <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5">
                Regret happens after the missed action. The opportunity exists
                before it.{" "}
                <span className="text-white">What if personal care could intervene at the moment action is needed?</span>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-xs text-zinc-500 mt-10">
              Source: L'Oréal / La Roche-Posay HELIOS study. Ipsos online-panel survey, ~17,001
              adults, 17 countries (Sept–Oct 2021), published in the{" "}
              <a href="https://doi.org/10.1111/jdv.19421" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                Journal of the European Academy of Dermatology and Venereology
              </a>. DOI: 10.1111/jdv.19421.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY PEOPLE FAIL */}
      <section className="border-b border-zinc-800" data-testid="science-barriers">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">Why People Fail to Act</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
              The barrier is behavioral,{" "}
              <span className="text-accent">not performance.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 max-w-4xl">
              <BarChart
                title="Reported barriers to sunscreen use"
                max={60}
                testid="science-chart-barriers"
                bars={[
                  { label: "Forget to bring / apply", value: 52.1, color: "#ff2d78", highlight: true },
                  { label: "Dislike feel or smell", value: 24.2, color: "#6b7280" },
                  { label: "Cost", value: 21.7, color: "#6b7280" },
                  { label: "Inconvenience", value: 20.3, color: "#6b7280" },
                ]}
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-14 max-w-3xl">
              Researchers identified integrating sunscreen into routines and using
              cues to action as strategies for supporting reapplication. The
              friction WearSpray targets is remembering, knowing when, having it
              available, stopping what you are doing, finding the product, and
              applying it.
            </p>
            <p className="font-body text-xs text-zinc-500 mt-8">
              Source: Hall SM, Steadman M, Major S, Oldham MF, Mitchell H (2026).{" "}
              <a href="https://doi.org/10.1016/j.focus.2026.100495" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                Sunscreen Use Barriers in White U.S. Adults: Gender, Generation, and Skin Phototype Differences.
              </a>{" "}
              <em>AJPM Focus</em>. DOI: 10.1016/j.focus.2026.100495. Cross-sectional online panel survey of 739 White U.S. adults.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PROMPTING EVIDENCE */}
      <section className="border-b border-zinc-800 bg-[#0a0a0a]" data-testid="science-prompting">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">The Evidence That Prompting Can Matter</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
              Contextual prompting{" "}
              <span className="text-accent">nearly doubled adherence.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
              A randomized controlled study compared a sunscreen control group with
              a group receiving contextual mobile reminders that combined local
              weather with a prompt to apply sunscreen.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 max-w-3xl">
              <BarChart
                title="Average sunscreen adherence"
                max={60}
                testid="science-chart-prompting"
                bars={[
                  { label: "Without prompting", value: 30.0, color: "#6b7280" },
                  { label: "With contextual prompt", value: 56.1, color: "#00e5ff", highlight: true },
                ]}
              />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-14 max-w-3xl">
              The prompted group was nearly twice as adherent, yet the study used no
              advanced AI. It did not know the user's habits or activity, did not
              know when they had last applied, and did not put sunscreen on the
              wrist. A contextual prompt alone still changed behavior.
            </p>
            <p className="font-body text-xs text-zinc-500 mt-8">
              Source: Armstrong AW, Watson AJ, Makredes M, Frangos JE, Kimball AB, Kvedar JC (2009).{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/19917951/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                Text-message reminders to improve sunscreen use: a randomized, controlled trial using electronic monitoring.
              </a>{" "}
              <em>Archives of Dermatology</em>, 145(11), 1230–1236. DOI: 10.1001/archdermatol.2009.269.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CLOSED LOOP */}
      <section className="border-b border-zinc-800" data-testid="science-closed-loop">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">WearSpray Closes the Loop</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06]">
              Sense <span className="text-zinc-600">→</span> Understand{" "}
              <span className="text-zinc-600">→</span> Prompt{" "}
              <span className="text-zinc-600">→</span>{" "}
              <span style={{ color: "#ff2d78" }}>Act</span>{" "}
              <span className="text-zinc-600">→</span>{" "}
              <span style={{ color: "#b794f6" }}>Learn.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mt-16">
            {LOOP.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.08}>
                <div className="h-full border border-zinc-800 bg-[#111] p-7" data-testid={`science-loop-${s.k.toLowerCase()}`}>
                  <div className="w-12 h-12 rounded-full border flex items-center justify-center" style={{ borderColor: s.c, color: s.c }}>
                    <s.Icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-heading text-xl font-medium tracking-tight mt-5" style={{ color: s.c }}>{s.k}</h3>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SkinIntelligence />

      {/* PRECEDENT */}
      <section className="border-b border-zinc-800 bg-[#0a0a0a]" data-testid="science-precedent">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">Strategic Precedent</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.06] max-w-4xl">
              Wearable UV sensing has already{" "}
              <span className="text-accent">changed behavior.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
              La Roche-Posay and L'Oréal developed My UV Patch and later My Skin
              Track UV to monitor UV exposure. Follow-up consumer studies around My
              UV Patch showed:
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 mt-16">
            <Reveal>
              <BigStat value="34" caption="of participants applied sunscreen more frequently" color="#00e5ff" size="lg" testid="science-stat-apply" />
            </Reveal>
            <Reveal delay={0.1}>
              <BigStat value="37" caption="sought shade more frequently" color="#3ad6a0" size="lg" testid="science-stat-shade" />
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-16 max-w-3xl">
              Wearable sensing can influence protective behavior. But a step was
              still missing: the sensor could Sense, Understand and Prompt, yet the
              user still had to find the sunscreen.{" "}
              <span className="text-white">WearSpray proposes the fourth step: Act.</span>
            </p>
            <p className="font-body text-xs text-zinc-500 mt-8">
              Source: L'Oréal / La Roche-Posay, My UV Patch consumer research
              (company-reported post-launch survey, not an independently
              peer-reviewed study).{" "}
              <a href="https://www.prnewswire.com/news-releases/la-roche-posay-launches-the-first-ever-wearable-skin-sensor-300308532.html" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                Source release
              </a>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HYPOTHESIS + DOWNLOAD */}
      <section className="border-b border-zinc-800" data-testid="science-hypothesis">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-6">A Credible Hypothesis</span>
            <p className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-[1.25] max-w-4xl text-white">
              These studies do not prove WearSpray will improve adherence. They
              establish the problem, show that contextual prompting can influence
              behavior, and demonstrate that wearable UV awareness has already
              changed behavior.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
              That leaves a credible hypothesis: if awareness and contextual
              prompting can influence behavior, removing the final barrier between
              the prompt and the product may create an even more actionable
              personal care experience.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex flex-col sm:flex-row gap-4 mt-12">
              <a
                href={WHITEPAPER}
                download
                data-testid="science-download-whitepaper"
                className="group inline-flex items-center gap-2.5 font-body text-sm font-medium bg-[#00e5ff] text-black px-7 py-4 hover:bg-[#00b3cc] transition-colors justify-center"
              >
                <Download size={17} />
                Download the Research White Paper
              </a>
              <Link
                to="/contact?topic=Clinical%20Collaboration"
                data-testid="science-partner-cta"
                className="group inline-flex items-center gap-2 font-body text-sm font-medium border border-zinc-700 text-white px-7 py-4 hover:border-zinc-500 transition-colors justify-center"
              >
                Discuss a Pilot
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-16 border-t border-zinc-800 pt-10">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">Sources</p>
              <ul className="space-y-2 max-w-4xl">
                {[
                  "Norman et al., 2023. Food and Chemical Toxicology. DOI: 10.1016/j.fct.2023.114093. Affiliations: Beiersdorf, L'Oréal USA, P&G, Revlon, Personal Care Products Council.",
                  "L'Oréal / La Roche-Posay HELIOS epidemiological study (Ipsos panel, ~17,001 people, 17 countries, 2021). JEADV. DOI: 10.1111/jdv.19421.",
                  "Hall SM, Steadman M, Major S, Oldham MF, Mitchell H, 2026. Sunscreen Use Barriers in White U.S. Adults. AJPM Focus. DOI: 10.1016/j.focus.2026.100495.",
                  "Armstrong AW, Watson AJ, Makredes M, Frangos JE, Kimball AB, Kvedar JC, 2009. Text-message reminders to improve sunscreen use. Archives of Dermatology, 145(11), 1230–1236. DOI: 10.1001/archdermatol.2009.269.",
                  "L'Oréal / La Roche-Posay My UV Patch consumer research (company-reported post-launch survey).",
                ].map((s, i) => (
                  <li key={i} className="font-body text-xs text-zinc-500 leading-relaxed">• {s}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
